import { RegistrationSchemaStep2 } from 'app/helpers/validators';
import React, { useEffect, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { Dropdown } from 'app/components/common/formik/Dropdown';
import { Checkbox } from 'app/components/common/formik/Checkbox';
import FieldError from 'app/components/common/formik/FieldError';
import { Link } from 'react-router-dom';
import * as Routes from 'app/constants/Routes';
import { Card, Heading, Button as MaximusButton } from 'mui';
import { trackEvent } from 'app/utils/useSegmentTracking';
import { apiRequest } from 'app/api';
import states from 'app/constants/geo-states.json';

const msoTermsOfUseLink = '/terms-of-use';
const msoPrivacyPolicyLink = '/privacy-policy';
const pcInformedConsentLink = '/telehealth-informed-consent';
const pcPrivacyPracticesLink = '/notice-privacy-practices';

function findCodeByName(name: string) {
  const state = Object.values(states).find((state) => state.name === name);
  return state ? state.code : null;
}

type Props = {
  experimentVariant: string;
  initialValues: {
    state: string;
    over_18: boolean;
    agree: boolean;
    pc_opt_in: boolean;
    mso_opt_in: boolean;
  };
  onSubmit: (values: any) => void;
  trackCompletionEvent?: (values: any) => void | null;
};
const LocationAndAge = ({ experimentVariant, initialValues, onSubmit, trackCompletionEvent }: Props) => {
  const stateSelectionExperiment = experimentVariant === 'registration_state_selection_variation_1';
  const [detectedState, setDetectedState] = useState<string | null>('');
  const handleStateDropdownOpen = () => {
    trackEvent('Clicked State Selection Drop Down');
  };
  const handleStateDropdownClick = () => {
    trackEvent('Selected State');
  };
  useEffect(() => {
    apiRequest('GuestController', 'GET', '/api/guests/state')
      .then((response) => {
        if (response.json && response.json.state) {
          setDetectedState(findCodeByName(response.json.state));
        }
      })
      .catch((error) => {
        console.error('Failed to fetch state:', error);
      });
  }, []);
  return (
    <Formik
      initialValues={{
        ...initialValues,
        agree: initialValues.pc_opt_in && initialValues.mso_opt_in,
      }}
      onSubmit={({ agree, ...values }) => {
        onSubmit({
          ...values,
          pc_opt_in: agree,
          mso_opt_in: agree,
        });
        if (trackCompletionEvent) {
          trackCompletionEvent({
            state: values?.state,
          });
        }
      }}
      validationSchema={RegistrationSchemaStep2}
    >
      {({ isSubmitting, setFieldValue }) => {
        useEffect(() => {
          if (detectedState) {
            setFieldValue('state', detectedState);
          }
        }, [detectedState]);

        return (
          <Form>
            <Card>
              <Card.Body>
                <div>
                  <Heading headingLevel="h3" className="mb-2">
                    {stateSelectionExperiment ? 'Can we ship to you?' : 'What’s your location and age?'}
                  </Heading>
                  <p>
                    {stateSelectionExperiment
                      ? 'Some products are only available in specific states. Select your state to see products available to you'
                      : 'Provide your age and state to confirm treatment eligibility and shipping availability if qualified.'}
                  </p>
                </div>
                <Dropdown
                  id="state"
                  onMenuOpen={handleStateDropdownOpen}
                  onClick={handleStateDropdownClick}
                  name="state"
                  emptyValue="Select Your State"
                  position="bottom"
                  label={stateSelectionExperiment ? 'U.S. State' : 'State'}
                />
                <Checkbox
                  id="over_18"
                  name="over_18"
                  label="I am at least 18 years old."
                  labelClassName="pt-[2px]"
                  error={<Field name="over_18" component={FieldError} />}
                />
                <Checkbox
                  id="agree"
                  name="agree"
                  error={<Field name="agree" component={FieldError} />}
                  label={
                    <>
                      By clicking this box, I acknowledge that I have read, understood, and agree to the{' '}
                      <a rel="noreferrer" href={msoTermsOfUseLink} target="_blank">
                        Terms of Use
                      </a>
                      ,{' '}
                      <a rel="noreferrer" href={msoPrivacyPolicyLink} target="_blank">
                        Privacy Policy
                      </a>
                      ,{' '}
                      <a rel="noreferrer" href={pcInformedConsentLink} target="_blank">
                        Telehealth Informed Consent
                      </a>
                      , and acknowledge the{' '}
                      <a rel="noreferrer" href={pcPrivacyPracticesLink} target="_blank">
                        Notice of Privacy Practices
                      </a>
                    </>
                  }
                />
                <MaximusButton
                  disabled={isSubmitting}
                  type="submit"
                  testid="registration-step-2-submit"
                  className="button-next"
                >
                  Next
                </MaximusButton>
                <p>
                  Already have an account? <Link to={Routes.Login}>Sign In</Link>
                </p>
              </Card.Body>
            </Card>
          </Form>
        );
      }}
    </Formik>
  );
};

export default LocationAndAge;
